import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tooltip, Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import {
  useStorageAction, type ProfileCheck, type ProfileIn, type StorageAdmin, type StorageProfile, type StorageProvider,
  type StorageView,
} from '@/api/storage-admin';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DialogClose } from '@/components/DialogClose';
import type { FieldDef, FieldValues } from '@/components/fields';
import { LocalDataTable } from '@/components/grid/LocalDataTable';
import { changedValues, FieldsDialog, HealthChip, serverError, whole } from './common';
import { NEW_PROFILE, profileFields } from './configFields';

const CORS_FIELDS: FieldDef[] = [
  { type: 'tags', name: 'origins', label: 'Sites that upload to this bucket', required: true, grid: { xs: 12 },
    helperText: 'The workspace’s web address, e.g. https://acme.example.com. Each one is added to the bucket’s CORS rules.' },
];
const LIFECYCLE_FIELDS: FieldDef[] = [
  whole('days', 'Move files to Glacier Instant Retrieval after (days)', 30, 3650, { required: true, grid: { xs: 12 },
    helperText: 'Old proofs are rarely opened; this storage class costs far less and still opens instantly.' }),
];
const LIFECYCLE_INITIAL = { days: 180 };

type Dialogs =
  | { kind: 'edit'; profile: StorageProfile | null }
  | { kind: 'delete' | 'relocate' | 'cors' | 'lifecycle'; profile: StorageProfile }
  | { kind: 'check'; profile: StorageProfile; result: ProfileCheck };

/** The S3 servers a workspace can store files on. */
export function ProfilesSection({ admin, view, corsOrigins }: {
  admin: StorageAdmin;
  view: StorageView;
  /** The workspace's own address, offered when the operator writes the bucket's CORS rules. */
  corsOrigins?: string[];
}) {
  const [dialog, setDialog] = useState<Dialogs | null>(null);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const close = () => setDialog(null);
  const editable = view.profiles_editable;

  const save = useStorageAction(admin, ({ id, body }: { id: string | null; body: ProfileIn }) => admin.saveProfile(id, body));
  const remove = useStorageAction(admin, (id: string) => admin.deleteProfile(id));
  const check = useStorageAction(admin, (id: string) => admin.testProfile(id));
  const relocate = useStorageAction(admin, (id: string) => admin.relocate!(id));
  const cors = useStorageAction(admin, ({ id, origins }: { id: string; origins: string[] }) => admin.applyCors!(id, origins));
  const lifecycle = useStorageAction(admin, ({ id, days }: { id: string; days: number }) => admin.applyLifecycle!(id, days));

  const run = async (label: string, work: () => Promise<unknown>) => {
    setError(undefined);
    setNotice(undefined);
    try {
      await work();
      setNotice(label);
    } catch (e) {
      setError(serverError(e).message);
    }
  };

  const test = (profile: StorageProfile) => {
    setError(undefined);
    check.mutateAsync(profile.id).then(
      (result) => setDialog({ kind: 'check', profile, result }),
      (e) => setError(serverError(e).message),
    );
  };

  const columns: GridColDef<StorageProfile>[] = [
    {
      field: 'name', headerName: 'Server', minWidth: 200, flex: 1,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
          <span>{row.name}</span>
          {row.id === view.platform.active_profile_id && <Chip size="small" color="primary" label="In use" />}
          {!row.is_active && <Chip size="small" label="Off" />}
        </Stack>
      ),
    },
    { field: 'provider', headerName: 'Provider', minWidth: 110 },
    { field: 'bucket', headerName: 'Bucket', minWidth: 160, cellClassName: 'mono' },
    { field: 'region', headerName: 'Region', minWidth: 110 },
    {
      field: 'health_status', headerName: 'Health', minWidth: 120,
      renderCell: ({ row }) => (
        <Tooltip title={row.health_error ?? ''}><span><HealthChip status={row.health_status} /></span></Tooltip>
      ),
    },
    {
      field: 'actions', headerName: '', minWidth: 330, sortable: false, filterable: false,
      renderCell: ({ row }) => editable && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}
               onClick={(e) => e.stopPropagation()}>
          <Button size="small" disabled={check.isPending} onClick={() => test(row)}>Test</Button>
          {admin.applyCors && <Button size="small" onClick={() => setDialog({ kind: 'cors', profile: row })}>CORS</Button>}
          {admin.applyLifecycle && (
            <Button size="small" onClick={() => setDialog({ kind: 'lifecycle', profile: row })}>Archive rule</Button>
          )}
          {admin.relocate && row.id !== view.platform.active_profile_id && (
            <Button size="small" onClick={() => setDialog({ kind: 'relocate', profile: row })}>Move files here</Button>
          )}
          <Button size="small" color="error" onClick={() => setDialog({ kind: 'delete', profile: row })}>Delete</Button>
        </Stack>
      ),
    },
  ];

  const editing = dialog?.kind === 'edit' ? dialog.profile : null;
  const fields = useMemo(() => profileFields(editing), [editing]);
  const initial = useMemo<FieldValues>(
    () => (editing ? { ...editing } : { ...NEW_PROFILE, ...view.provider_defaults.AWS }),
    [editing, view.provider_defaults],
  );
  const corsInitial = useMemo(() => ({ origins: corsOrigins ?? [] }), [corsOrigins]);

  return (
    <Stack spacing={2}>
      {!editable && <Alert severity="info">S3 servers for this workspace are managed by the platform operator.</Alert>}
      {error && <Alert severity="error" onClose={() => setError(undefined)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(undefined)}>{notice}</Alert>}
      <LocalDataTable
        rows={view.profiles} columns={columns} getRowId={(r) => r.id}
        searchText={(r) => `${r.name} ${r.bucket} ${r.provider}`} searchPlaceholder="Search servers"
        noun={['server', 'servers']}
        onRowClick={editable ? (r) => setDialog({ kind: 'edit', profile: r }) : undefined}
        emptyMessage="No S3 servers yet. Files stay on this server until one is added and chosen."
        toolbarEnd={editable && (
          <Button variant="contained" size="small" onClick={() => setDialog({ kind: 'edit', profile: null })}>
            Add server
          </Button>
        )}
      />

      <FieldsDialog
        open={dialog?.kind === 'edit'} title={editing ? `Edit ${editing.name}` : 'Add an S3 server'}
        fields={fields} initial={initial} onClose={close}
        onChange={(name, value, form) => {
          form.setValue(name, value);
          // A new server takes the chosen provider's usual settings; an existing one keeps what it has.
          if (name === 'provider' && !editing) {
            Object.entries(view.provider_defaults[value as StorageProvider] ?? {}).forEach(([k, v]) => form.setValue(k, v));
          }
        }}
        onSubmit={(values) => save.mutateAsync({
          id: editing?.id ?? null,
          body: changedValues(fields, editing ? initial : {}, values) as ProfileIn,
        })}
      />

      {dialog?.kind === 'cors' && (
        <FieldsDialog open title={`CORS for ${dialog.profile.bucket}`} fields={CORS_FIELDS}
          initial={corsInitial} submitLabel="Apply" onClose={close}
          onSubmit={async (v) => {
            await cors.mutateAsync({ id: dialog.profile.id, origins: v.origins as string[] });
            setNotice('CORS rules written to the bucket.');
          }} />
      )}
      {dialog?.kind === 'lifecycle' && (
        <FieldsDialog open title={`Archive rule for ${dialog.profile.bucket}`} fields={LIFECYCLE_FIELDS}
          initial={LIFECYCLE_INITIAL} submitLabel="Apply" onClose={close}
          onSubmit={async (v) => {
            const r = await lifecycle.mutateAsync({ id: dialog.profile.id, days: v.days as number });
            setNotice(`Files under ${r.prefix}/ move to Glacier Instant Retrieval after ${r.days} days.`);
          }} />
      )}
      <ConfirmDialog
        open={dialog?.kind === 'delete'} destructive title="Delete this server?" confirmLabel="Delete"
        busy={remove.isPending} onCancel={close}
        description={dialog?.kind === 'delete' && (
          <>Delete <b>{dialog.profile.name}</b>? A server that still holds files or is in use cannot be deleted.</>
        )}
        onConfirm={() => dialog?.kind === 'delete'
          && run('Server deleted.', () => remove.mutateAsync(dialog.profile.id)).then(close)} />
      <ConfirmDialog
        open={dialog?.kind === 'relocate'} title="Move every file to this server?" confirmLabel="Start moving"
        busy={relocate.isPending} onCancel={close}
        description={dialog?.kind === 'relocate' && (
          <>The background worker copies each file to <b>{dialog.profile.name}</b>, checks it, then switches the
            workspace over. Files stay readable throughout.</>
        )}
        onConfirm={() => dialog?.kind === 'relocate'
          && run('Moving files — progress shows in Logs.', () => relocate.mutateAsync(dialog.profile.id)).then(close)} />
      {dialog?.kind === 'check' && <CheckResult profile={dialog.profile} result={dialog.result} onClose={close} />}
    </Stack>
  );
}

function CheckResult({ profile, result, onClose }: { profile: StorageProfile; result: ProfileCheck; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Test of {profile.name} <DialogClose onClose={onClose} /></DialogTitle>
      <DialogContent dividers>
        <Alert severity={result.ok ? 'success' : 'error'} sx={{ mb: 1.5 }}>
          {result.ok ? 'The server accepted a test file and gave it back.' : 'The server could not be used.'}
        </Alert>
        <Stack spacing={1}>
          {result.steps.map((s) => (
            <Box key={s.step} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              {s.ok ? <CheckCircleIcon fontSize="small" color="success" /> : <ErrorIcon fontSize="small" color="error" />}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{s.step}</Typography>
                {s.detail && <Typography variant="caption" color="text.secondary">{s.detail}</Typography>}
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
